/**
 * Layout for the inside of a side sheet: `SheetHeader` → `SheetBody` →
 * `SheetActions`.
 *
 * Every action that acts on what the sheet shows (Save, Cancel, Edit,
 * Delete, Deactivate, Connect, Start call, …) lives in `SheetActions`,
 * never in the header and never at the end of the body:
 *
 * - The header's top-right corner belongs to the sheet's close X.
 * - The footer is pinned to the bottom of the viewport, so the actions are
 *   always in the same place, however long or short the body is. Only
 *   `SheetBody` scrolls; `SheetContent` itself never does.
 * - Toasts sit just above the footer (`TOAST_OFFSET` in `routes/__root.tsx`,
 *   which reads the footer's live height), so a toast never covers an action.
 *
 * Controls that act on one row or section inside the body (a row's remove
 * icon, an "Add" next to a section title) stay where they are.
 *
 * `sheet-layout.test.ts` scans the source to keep new sheets on this layout.
 */
import { useLayoutEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import {
  SheetClose,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { SHEET_ACTIONS_HEIGHT_VAR } from '@/lib/toast-offset';
import { cn } from '@/lib/utils';

/** The scrolling middle of a sheet. */
export function SheetBody({
  className,
  ...props
}: React.ComponentProps<'div'>): React.ReactElement {
  return (
    <div
      data-slot="sheet-body"
      className={cn('min-h-0 flex-1 overflow-y-auto px-4 pb-4', className)}
      {...props}
    />
  );
}

/**
 * A sheet whose record is still loading, under the title it is about to show.
 * A sheet opened straight into edit shows this — never the view with its Edit
 * button — until the record arrives.
 */
export function SheetLoading({
  title,
  className,
}: {
  title: string;
  /** Horizontal padding to match the sheet it stands in for (e.g. `px-6`). */
  className?: string;
}): React.ReactElement {
  return (
    <>
      <SheetHeader className={className}>
        <SheetTitle>{title}</SheetTitle>
        <SheetDescription className="sr-only">Loading</SheetDescription>
      </SheetHeader>
      <SheetBody className={className}>
        <p className="text-sm text-muted-foreground">Loading...</p>
      </SheetBody>
    </>
  );
}

interface SheetActionsProps {
  /** Left side: destructive or secondary actions (Delete, Deactivate, Disconnect, Test). */
  start?: React.ReactNode;
  /** Right side: Cancel, then the primary action. */
  children?: React.ReactNode;
  className?: string;
}

/**
 * Cancel that closes the sheet exactly as its X does (through the sheet's own
 * `onOpenChange`). Put it before a primary action that would otherwise sit
 * alone on the right — a view's Edit or Reactivate, a drawer's Start call.
 */
export function SheetCancel(): React.ReactElement {
  return (
    <SheetClose asChild>
      <Button type="button" variant="outline">
        Cancel
      </Button>
    </SheetClose>
  );
}

/**
 * Every mounted footer's height. Two can be on screen at once — one sheet
 * sliding out while the next slides in — so the tallest is published and the
 * variable goes only when the last footer does.
 */
const footerHeights = new Map<object, number>();

function publishFooterHeight(): void {
  const root = document.documentElement;
  if (footerHeights.size === 0) root.style.removeProperty(SHEET_ACTIONS_HEIGHT_VAR);
  else root.style.setProperty(SHEET_ACTIONS_HEIGHT_VAR, `${Math.max(...footerHeights.values())}px`);
}

/** The pinned footer that holds every action of a sheet. */
export function SheetActions({
  start,
  children,
  className,
}: SheetActionsProps): React.ReactElement {
  const ref = useRef<HTMLDivElement>(null);

  // Publishes the footer's live height so the toaster sits above it (see
  // `lib/toast-offset.ts`).
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const key = {};
    const measure = (): void => {
      footerHeights.set(key, el.offsetHeight);
      publishFooterHeight();
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => {
      observer.disconnect();
      footerHeights.delete(key);
      publishFooterHeight();
    };
  }, []);

  return (
    <SheetFooter
      ref={ref}
      className={cn(
        'shrink-0 flex-row flex-wrap items-center justify-between gap-2 border-t',
        className
      )}
    >
      <div className="flex flex-wrap items-center gap-2">{start}</div>
      <div className="ml-auto flex flex-wrap items-center justify-end gap-2">{children}</div>
    </SheetFooter>
  );
}
